export default interface SchoolYear {
  id: string
  type: "school-year"
  attributes: {
    title: string
    longAbbreviation: string
    shortAbbreviation: string
    years: Array<number>
  }
  meta: {}
}

export interface CreatePlanbookSchoolYearOption {
  readonly id: string
  readonly displayName: string
  readonly schoolYearId: string
  readonly semesterTitle: string
  readonly season: "FULL_YEAR" | "SUMMER"
  readonly startDate: string
  readonly endDate: string
}

// ----------
// CURRENT SCHOOL YEAR CONSTANTS
//
// Computed based off of the user's *local* time (including their time zone and clock inaccuracies).
// ----------

const currentSchoolYear = currentYearBeginning(7, 1)
// Allow creating planbooks for next school year beginning March 1.
// This is also when you can no longer create planbooks for the previous school year.
const newPlanbookYear = currentYearBeginning(3, 1)

/**
 * The year to show by default when you are viewing planbooks by year (e.g. on the user's home page).
 */
export const CURRENT_SCHOOL_YEAR_ID = schoolYearId(currentSchoolYear)

/**
 * All the years to choose from when you are viewing planbooks by year (e.g. on a school's home page).
 */
export const ALL_SCHOOL_YEAR_IDS: readonly string[] = schoolYearIdRange(2012, newPlanbookYear)

/**
 * Year/semester options for a new (created/copied) planbook.
 */
export const CREATE_PLANBOOK_SCHOOL_YEAR_OPTIONS = [
  // 1/1 - 3/1: The previous school year.
  // 3/1 - 12/31: The school year beginning the previous calendar year.
  createPlanbookFullYearOption(newPlanbookYear - 1),
  // The summer semester occurring this calendar year.
  createPlanbookSummerOption(currentYearBeginning(1, 1)),
  // 1/1 - 3/1: The current school year.
  // 3/1 - 12/31: The school year beginning this calendar year.
  createPlanbookFullYearOption(newPlanbookYear),
]

/**
 * The default Option object for a new (created/copied) planbook:
 * the last one, corresponding to the newest school year.
 *
 * The complete set of options is CREATE_PLANBOOK_SCHOOL_YEAR_OPTIONS.
 */
export const NEW_PLANBOOK_DEFAULT_SCHOOL_YEAR_OPTION =
  CREATE_PLANBOOK_SCHOOL_YEAR_OPTIONS[CREATE_PLANBOOK_SCHOOL_YEAR_OPTIONS.length - 1]

/**
 * The default school year to copy *from* when you select Copy Planbook:
 * the year before NEW_PLANBOOK_DEFAULT_SCHOOL_YEAR_ID.
 *
 * The complete set of options is ALL_SCHOOL_YEAR_IDS.
 */
export const COPIED_PLANBOOK_DEFAULT_SCHOOL_YEAR_ID = schoolYearId(newPlanbookYear - 1)

// ----------
// Helper functions
// ----------

/**
 * Returns the current year if now is after the cutoff date, else the previous year.
 */
function currentYearBeginning(cutoffMonth: number, cutoffDay: number): number {
  const date = new Date()
  const year = date.getFullYear()
  // getMonth() is 0-indexed, so convert the 1-indexed cutoffMonth to compare.
  const month = date.getMonth() + 1
  const isOnOrAfterCutoff =
    month > cutoffMonth || (month === cutoffMonth && date.getDate() >= cutoffDay)
  return isOnOrAfterCutoff ? year : year - 1
}

/**
 * Returns the full-year schoolYearId that **starts** in the given year.
 */
function schoolYearId(year: number): string {
  return `SY${year}${year + 1}`
}

/**
 * Returns the full-year schoolYearIds starting in years [start, end], in reverse order.
 */
function schoolYearIdRange(start: number, end: number): string[] {
  const ans: string[] = []
  for (let year = end; year >= start; year--) {
    ans.push(schoolYearId(year))
  }
  return ans
}

/**
 * Returns the full-year CreatePlanbookSchoolYearOption that **starts** in the given year.
 */
function createPlanbookFullYearOption(year: number): CreatePlanbookSchoolYearOption {
  return {
    id: `FULL_YEAR_${year}_${year + 1}`,
    displayName: `${year}-${year + 1}`,
    schoolYearId: schoolYearId(year),
    semesterTitle: "Full Year",
    season: "FULL_YEAR",
    startDate: `${year}-07-01`,
    endDate: `${year + 1}-06-30`,
  }
}

/**
 * Returns the summer CreatePlanbookSchoolYearOption that is in the given **calendar** year
 * (hence follows the school year beginning in the *previous* year.).
 */
function createPlanbookSummerOption(year: number): CreatePlanbookSchoolYearOption {
  return {
    id: `SUMMER_${year}`,
    displayName: `Summer ${year}`,
    // Use the schoolYearId of the school year *before* the summer.
    schoolYearId: schoolYearId(year - 1),
    semesterTitle: "Summer",
    season: "SUMMER",
    startDate: `${year}-05-01`,
    endDate: `${year}-09-30`,
  }
}
